/**
 * UserProfile Screen
 *
 * Displays a user's profile with their own theme applied in an isolated container.
 * Does NOT modify global theme — uses scoped inline styles from the viewed user's palette.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useThemeColors } from '@/styles/colors';
import { typography, spacing, radius, layout } from '@/styles/design-tokens';
import { getTheme, hasTheme, applyVariant } from '@/styles/colors/themes';
import { parseStoredTheme } from '@/styles/theme/ThemeContext';
import {
  getPlayfulTheme,
  hasPlayfulTheme,
  PlayfulThemeName,
} from '@/styles/playful';
import { MASCOT_COMPONENTS } from '@/components/core/ThemeMascot/mascots';
import { Avatar } from '@/components/ui/avatar';
import { ShareButton } from '@/components/core/ShareButton/ShareButton';
import { MyProgress } from '@/components/core';
import { useMyProgressQuery, useUserProfileQuery } from '@/hooks/queries';
import { DeepLinkEntity } from '@/utils/deep-linking';
import { MOOD_OPTIONS } from '@/types/dashboard';

interface UserProfileProps {
  userId: string;
  onBoardClick?: (boardId: string) => void;
  onProfileLoad?: (displayName: string) => void;
}

const getTagDisplay = (
  c: ReturnType<typeof useThemeColors>
): Record<string, { label: string; color: string }> => ({
  primary: { label: 'Primary', color: c.purple },
  secondary: { label: 'Secondary', color: c.teal },
  personal: { label: 'Personal', color: c.coral },
});

function ProfileHeaderSkeleton({
  colors,
}: {
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        marginBottom: spacing.lg,
      }}
    >
      {/* Avatar skeleton */}
      <div
        className="animate-pulse"
        style={{
          width: 64,
          height: 64,
          borderRadius: radius.full,
          background: `linear-gradient(90deg, ${colors.border.light} 0%, ${colors.border.default} 50%, ${colors.border.light} 100%)`,
          flexShrink: 0,
        }}
      />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.xs,
        }}
      >
        {/* Display name */}
        <div
          className="animate-pulse"
          style={{
            height: '1.25rem',
            width: '8rem',
            borderRadius: radius.sm,
            background: `linear-gradient(90deg, ${colors.border.light} 0%, ${colors.border.default} 50%, ${colors.border.light} 100%)`,
          }}
        />
        {/* Username */}
        <div
          className="animate-pulse"
          style={{
            height: '0.875rem',
            width: '5rem',
            borderRadius: radius.sm,
            background: `linear-gradient(90deg, ${colors.border.light} 0%, ${colors.border.default} 50%, ${colors.border.light} 100%)`,
          }}
        />
        {/* Role badge */}
        <div
          className="animate-pulse"
          style={{
            height: '1.25rem',
            width: '3.5rem',
            borderRadius: radius.full,
            background: `linear-gradient(90deg, ${colors.border.light} 0%, ${colors.border.default} 50%, ${colors.border.light} 100%)`,
          }}
        />
      </div>
    </div>
  );
}

export default function UserProfile({
  userId,
  onBoardClick,
  onProfileLoad,
}: UserProfileProps) {
  const fallbackColors = useThemeColors();

  const { data: profile, isLoading } = useUserProfileQuery(userId);

  // Notify parent when profile first loads
  useEffect(() => {
    if (profile?.user.display_name) {
      onProfileLoad?.(profile.user.display_name);
    }
  }, [profile?.user.display_name, onProfileLoad]);

  // Compute the viewed user's palette locally (theme isolation)
  const viewedPalette = useMemo(() => {
    const stored = profile?.preferences?.theme;
    if (!stored) return null;

    const { theme, variant } = parseStoredTheme(stored);
    if (!hasTheme(theme)) return null;

    return applyVariant(getTheme(theme).palette, variant);
  }, [profile?.preferences?.theme]);

  // Use viewed user's palette if available, otherwise fallback to current theme
  const colors = viewedPalette ?? fallbackColors;
  const TAG_DISPLAY = getTagDisplay(colors);

  // Resolve the viewed user's playful theme mascot
  const mascotStyleRef = useRef<HTMLStyleElement | null>(null);
  const viewedMascot = useMemo(() => {
    const pt = profile?.preferences?.playful_theme;
    if (!pt || !hasPlayfulTheme(pt)) return null;
    const def = getPlayfulTheme(pt as PlayfulThemeName);
    if (!def.metadata.mascotEmoji) return null;
    return {
      emoji: def.metadata.mascotEmoji,
      themeId: pt,
      idle: def.idle,
      keyframes: def.keyframes,
    };
  }, [profile?.preferences?.playful_theme]);

  // Inject the viewed user's playful keyframes
  useEffect(() => {
    if (!viewedMascot || viewedMascot.keyframes.length === 0) {
      if (mascotStyleRef.current) {
        mascotStyleRef.current.textContent = '';
      }
      return;
    }
    if (!mascotStyleRef.current) {
      const style = document.createElement('style');
      style.setAttribute('data-mascot-keyframes', 'true');
      document.head.appendChild(style);
      mascotStyleRef.current = style;
    }
    mascotStyleRef.current.textContent = viewedMascot.keyframes
      .map(kf => kf.css)
      .join('\n');
    return () => {
      if (mascotStyleRef.current) {
        mascotStyleRef.current.textContent = '';
      }
    };
  }, [viewedMascot]);

  // Cleanup mascot style on unmount
  useEffect(() => {
    return () => {
      if (mascotStyleRef.current) {
        mascotStyleRef.current.remove();
        mascotStyleRef.current = null;
      }
    };
  }, []);

  const { data: heatmapResult, isLoading: heatmapLoading } =
    useMyProgressQuery(userId);
  const heatmapData = heatmapResult?.my_progress;
  const heatmapWeeks = heatmapResult?.weeks ?? 5;

  const { user, preferences, boards } = profile ?? {};

  // Sort boards: primary → secondary → other tags → grouped (no tag) → non-tagged
  const sortedBoards = useMemo(() => {
    if (!boards) return [];
    const tagRank = (b: { tag?: string; group_id?: string }): number => {
      if (b.tag === 'primary') return 0;
      if (b.tag === 'secondary') return 1;
      if (b.tag) return 2;
      if (b.group_id) return 3;
      return 4;
    };
    return [...boards].sort((a, b) => {
      const rankDiff = tagRank(a) - tagRank(b);
      return rankDiff !== 0 ? rankDiff : a.title.localeCompare(b.title);
    });
  }, [boards]);

  return (
    <div
      style={{
        backgroundColor: colors.background,
        color: colors.dark,
        borderRadius: radius.lg,
        border: `1px solid ${colors.border.default}`,
        padding: spacing.lg,
      }}
    >
      {/* Header: avatar + name + share */}
      {isLoading ? (
        <ProfileHeaderSkeleton colors={fallbackColors} />
      ) : !profile ? (
        <p
          style={{ color: fallbackColors.error, fontSize: typography.sizes.sm }}
        >
          Failed to load profile.
        </p>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            marginBottom: spacing.lg,
          }}
        >
          <Avatar
            src={user!.profile_image}
            displayName={user!.display_name}
            userId={user!.id}
            size={64}
          />
          <div>
            <h3
              style={{
                fontSize: typography.sizes.lg,
                fontWeight: typography.weights.semibold,
                margin: 0,
                color: colors.text.primary,
              }}
            >
              {user!.display_name}
              {viewedMascot && (
                <span
                  aria-hidden
                  style={{
                    display: 'inline-block',
                    fontSize: typography.sizes.xl,
                    marginLeft: spacing.xs,
                    animationName: viewedMascot.idle.animationName,
                    animationDuration: viewedMascot.idle.animationDuration,
                    animationTimingFunction:
                      viewedMascot.idle.animationTimingFunction,
                    animationIterationCount:
                      viewedMascot.idle.animationIterationCount,
                  }}
                >
                  {(() => {
                    const SvgMascot = MASCOT_COMPONENTS[viewedMascot.themeId];
                    return SvgMascot ? <SvgMascot /> : viewedMascot.emoji;
                  })()}
                </span>
              )}
            </h3>
            <p
              style={{
                fontSize: typography.sizes.sm,
                margin: `${spacing.xs} 0 0`,
                color: colors.text.secondary,
              }}
            >
              @{user!.username}
            </p>
            <span
              style={{
                display: 'inline-block',
                marginTop: spacing.xs,
                fontSize: typography.sizes.xs,
                fontWeight: typography.weights.semibold,
                padding: `${spacing.xs} ${spacing.sm}`,
                borderRadius: radius.full,
                backgroundColor: colors.primary,
                color: colors.text.inverse,
                textTransform: 'uppercase',
                letterSpacing: '0.025em',
              }}
            >
              {user!.role}
            </span>
          </div>
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
            }}
          >
            {preferences?.mood &&
              (() => {
                const mood = MOOD_OPTIONS.find(m => m.id === preferences.mood);
                return mood ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: spacing.xs,
                      height: layout.moodWidget.height,
                      minWidth: layout.moodWidget.minWidth,
                      borderRadius: radius.sm,
                      border: `1px solid ${colors.border.default}`,
                      backgroundColor: colors.offWhite,
                      color: colors.text.primary,
                    }}
                  >
                    <span style={{ fontSize: typography.sizes['2xl'] }}>
                      {mood.emoji}
                    </span>
                    <span style={{ fontSize: typography.sizes.xs }}>
                      {mood.label}
                    </span>
                  </div>
                ) : null;
              })()}
            <ShareButton
              entity={DeepLinkEntity.USER}
              id={user!.id}
              ariaLabel="Copy profile link"
              iconColor={colors.icon.secondary}
            />
          </div>
        </div>
      )}

      {/* Activity heatmap — renders independently of profile load */}
      {!heatmapLoading && heatmapData && heatmapData.length > 0 && (
        <div style={{ marginBottom: spacing.lg }}>
          <h4
            style={{
              fontSize: typography.sizes.xs,
              fontWeight: typography.weights.semibold,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: colors.text.secondary,
              marginBottom: spacing.xs,
            }}
          >
            Heatmap
          </h4>
          <div
            style={{
              padding: spacing.sm,
              borderRadius: radius.sm,
              backgroundColor: colors.offWhite,
            }}
          >
            <MyProgress
              data={heatmapData}
              weeks={heatmapWeeks}
              colors={colors}
            />
          </div>
        </div>
      )}

      {/* Boards section — only rendered when profile loaded */}
      {profile && boards && (
        <div>
          <h4
            style={{
              fontSize: typography.sizes.xs,
              fontWeight: typography.weights.semibold,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: colors.text.secondary,
              marginBottom: spacing.xs,
            }}
          >
            Boards ({sortedBoards.length})
          </h4>
          {sortedBoards.length === 0 ? (
            <p
              style={{
                fontSize: typography.sizes.sm,
                color: colors.text.tertiary,
              }}
            >
              No boards yet.
            </p>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xs,
              }}
            >
              {sortedBoards.map(board => (
                <div
                  key={board.id}
                  role={onBoardClick ? 'button' : undefined}
                  tabIndex={onBoardClick ? 0 : undefined}
                  onClick={
                    onBoardClick ? () => onBoardClick(board.id) : undefined
                  }
                  onKeyDown={
                    onBoardClick
                      ? e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onBoardClick(board.id);
                          }
                        }
                      : undefined
                  }
                  style={{
                    padding: `${spacing.xs} ${spacing.sm}`,
                    borderRadius: radius.sm,
                    backgroundColor: colors.offWhite,
                    fontSize: typography.sizes.sm,
                    color: colors.text.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: '2rem',
                    cursor: onBoardClick ? 'pointer' : 'default',
                    transition: onBoardClick
                      ? 'background-color 0.15s'
                      : undefined,
                  }}
                  onMouseEnter={
                    onBoardClick
                      ? e => {
                          (
                            e.currentTarget as HTMLDivElement
                          ).style.backgroundColor = colors.border.default;
                        }
                      : undefined
                  }
                  onMouseLeave={
                    onBoardClick
                      ? e => {
                          (
                            e.currentTarget as HTMLDivElement
                          ).style.backgroundColor = colors.offWhite;
                        }
                      : undefined
                  }
                >
                  <span>{board.title}</span>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.xs,
                    }}
                  >
                    {board.tag && TAG_DISPLAY[board.tag] && (
                      <span
                        style={{
                          backgroundColor: TAG_DISPLAY[board.tag].color + '20',
                          color: TAG_DISPLAY[board.tag].color,
                          border: `1px solid ${TAG_DISPLAY[board.tag].color}40`,
                          padding: '0.0625rem 0.375rem',
                          borderRadius: radius.full,
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        {TAG_DISPLAY[board.tag].label}
                      </span>
                    )}
                    {board.group_id && (
                      <span
                        style={{
                          backgroundColor: colors.blue + '20',
                          color: colors.blue,
                          border: `1px solid ${colors.blue}40`,
                          padding: '0.0625rem 0.375rem',
                          borderRadius: radius.full,
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        Group
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
